// PAINT PICKER v2 — THE house color / gradient / texture standard.
// (Owner, 2026-07-20: "This color selector and just a texture button looks
// primitive… use the same color/texture/gradient code as a standard wherever
// we use color/texture/gradient." Then, same day, with the reference UI:
// "Better — we are getting closer… the window is slightly larger, and we
// have far more granular control over gradients." And: "we will change the
// content based on the tab selection, not have all 3" — ONE window, the
// Color/Gradient/Texture content swaps per tab, the footer persists.)
//
// ONE component family for the house FillValue model — the posterTools shape
// every recipe already stores:
//   '#hex' / 'rgba(r,g,b,a)'              (bare-string solid — the legacy law;
//                                          v2 adds PAINT OPACITY: alpha<100%
//                                          emits 'rgba()', opaque stays hex)
//   { type:'solid', color }
//   { type:'linear-gradient', stops:[{offset,color}…], angle }
//   { type:'radial-gradient', stops:[{offset,color}…] }        (no angle key)
//   { type:'texture', id, scale? }        (manifest ID, never a URL —
//                                          the texture portability law)
//   { type:'texture', src, scale? }       (v2: USER-IMPORTED texture — a
//                                          data-URL src. The ids-not-URLs law
//                                          governs MANIFEST textures; user
//                                          imports are inherently src-based
//                                          and a data URL is deployment-
//                                          independent by construction, so
//                                          portability holds either way.)
//
//   · PaintChip   — the compact trigger face (truthful solid/ramp/texture).
//   · PaintPicker — the panel: pill tabs "◐ Color | ▨ Gradient | ✳ Texture"
//                   (hosts filter via `allow`; single-pane hosts show no
//                   tabs), one pane at a time, persistent footer
//                   (Eyedropper · Clear · Apply).
//   · PaintField  — chip + body-portaled popover (viewport-clamped, centers
//                   like a modal on small screens).
//
// EMISSION CONTRACT (unchanged from v1 — hosts own the commit grammar):
//   onChange(next, {live})  — string for solids, FillValue object otherwise;
//     live=true on drag ticks (SV/hue/opacity pads, stop/midpoint/dial drags,
//     scale slider). House mapping: live solid → 'soon', live recipe →
//     'defer', discrete → 'now'.
//   onCommit()              — ONCE per drag stroke (pointer-up/blur):
//     map to history.commitNow so a drag lands ONE undo entry.
//
// FOOTER LAW (v2): changes apply LIVE exactly as v1 — there is NO staging.
//   · Apply  = commit-and-close: fires onCommit() and (popover hosts) closes.
//   · Clear  = revert-to-open: the value is snapshotted when the panel
//     MOUNTS (popover open / flyout open); Clear writes that snapshot back
//     through the live pipe — the styles-modal Revert law.
//
// GRADIENT MIDPOINTS (the "far more granular control" callout): the diamond
// between two adjacent stops edits that segment's interpolation bias.
// REPRESENTATION DECISION — the FillValue model stays PLAIN {stops[]}
// (exportability law): a bias ≠ 50% is BAKED AT WRITE TIME as ONE inserted
// intermediate stop at the bias position carrying the segment's 50/50 blend
// color (the CSS color-hint approximation). While THIS panel stays open it
// keeps the logical stops + biases apart so the diamonds stay live; a
// reopened/reloaded gradient reads baked stops as ordinary stops — deliberate,
// the saved JSON is plain fabric-ready stops and resolveFill/export never
// learn a new key.
//
// PAINT OPACITY (v2): the Color tab's Opacity slider is PAINT alpha. Solids
// emit plain 'rgba()' strings when alpha < 100% (fabric fills/strokes,
// per-char setSelectionStyles and canvas2d recipes all parse them; gradient
// stops carry per-stop alpha the same way). Engines that CANNOT carry alpha
// (three.js face colors, id-encoded plate/sides params, emboss integer hex
// math) receive opaque hex: those hosts pass `opaque` (hides the slider,
// clamps emissions through normalizeOpaqueHex) and solidHexFrom() now strips
// alpha for every derivation seam. See the alpha truth table in the
// integration notes.
//
// PERSISTED RINGS (localStorage, module-level pub/sub so every open picker
// refreshes together):
//   pseRecentPaints.v1   — last-8 any-kind ring (v1, unchanged).
//   pseUserSwatches.v1   — the SWATCHES "+" button saves the current solid
//                          (cap 12, dedupe; right-click / long-press removes).
//   pseUserGradients.v1  — the PRESETS gear saves the current gradient
//                          (cap 8; right-click / long-press removes).
//   pseUserTextures.v1   — Import Texture… (the Media-panel pipeline:
//                          FileReader → data URL, downscaled to ≤1024px —
//                          no server, no auth gate needed; removeBG's temp-R2
//                          hop exists only to feed the external enhance API).
//                          Cap 8; right-click / long-press removes the entry
//                          (the design keeps its embedded src regardless).
//
// EYEDROPPER (footer, first-class in v2): window.EyeDropper where the
// runtime has it; otherwise a canvas-sampling fallback — full-viewport
// overlay, click samples the fabric lower canvas at DPR, Esc cancels.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  rgbToHex, rgbToCmyk, cmykToRgb, rgbToHsv, hsvToRgb, parseHexInput,
  parseColor, formatColor, normalizeOpaqueHex, mixColors,
} from './colorMath.mjs';
import {
  GRADIENT_PRESETS, TEXTURES, TEXTURE_CATEGORIES, TEXTURES_FEATURED, textureById, textureUrl,
} from '../inspector/text/fillPresets';
import './paintPicker.css';
// PaintField chips open the ONE house paint modal (owner 2026-07-21). This is
// a runtime-only call (onClick), so the PaintField → modal → PaintPicker import
// cycle never evaluates circularly — the controller module holds no React.
import { openPaintModal, getRegisteredPaletteCanvas } from '../paintModalController';
// The color-system library (owner, 2026-07-21: recents + user galleries +
// poster palette): one shelf per pane, global per-kind memory in the
// colorLibrary store. noteRecent joins the SAME commit seams rememberPaint
// uses — never a live drag tick.
import LibraryShelf from './colorlib/LibraryShelf';
import { noteRecent, kindOfFill } from './colorlib/colorLibrary';

// ---------------------------------------------------------------------------
// palette — the house swatch set: 2 rows × 6 (the mock's grid). THEME row
// still exported for hosts that fold background tones in via `palette`.
export const HOUSE_SWATCHES = [
  '#000000', '#334155', '#64748b', '#ffffff', '#dc2626', '#ea580c',
  '#f59e0b', '#16a34a', '#0ea5e9', '#2563eb', '#7c3aed', '#db2777',
];
export const THEME_SWATCHES = [
  '#f8fafc', '#fef9c3', '#dcfce7', '#dbeafe', '#fce7f3', '#111827',
];
const DEFAULT_PALETTE = [...HOUSE_SWATCHES];

const colorOr = (v, fallback) => (parseColor(v) ? v : fallback);

// ---------------------------------------------------------------------------
// FillValue helpers (exported — hosts and faces share ONE reading of a paint)

/** 'solid' | 'gradient' | 'texture' | 'none'. */
export function paintKind(fv) {
  if (fv == null) return 'none';
  if (typeof fv === 'string') return 'solid';
  if (fv.type === 'solid') return 'solid';
  if (fv.type === 'linear-gradient' || fv.type === 'radial-gradient') return 'gradient';
  if (fv.type === 'texture') return 'texture';
  return 'none';
}

/** Normalized stops array for a gradient FillValue (legacy from/to honored). */
export function paintStops(fv) {
  return fv?.stops
    ?? (fv?.from
      ? [{ offset: 0, color: fv.from }, { offset: 1, color: fv.to ?? fv.from }]
      : [{ offset: 0, color: '#f97316' }, { offset: 1, color: '#ec4899' }]);
}

/** CSS preview of a gradient FillValue (the FillSection/ColorFlyout
 * convention — angle 0 = left→right). */
export function gradientCss(fv) {
  const stops = paintStops(fv)
    .map((s) => `${s.color} ${Math.round((s.offset ?? 0) * 100)}%`)
    .join(', ');
  return fv?.type === 'radial-gradient'
    ? `radial-gradient(circle, ${stops})`
    : `linear-gradient(${90 + (fv?.angle ?? 0)}deg, ${stops})`;
}

/** URL behind a texture FillValue: user imports carry src, manifest ids
 * re-derive (the id/src split — see header). */
export function textureSrcOf(fv) {
  if (fv?.type !== 'texture') return null;
  return fv.src ?? (fv.id ? textureUrl(fv.id) : null);
}

/** Style object rendering ANY FillValue truthfully (chip faces, previews). */
export function paintCss(fv, solidFallback = '#111827') {
  const kind = paintKind(fv);
  if (kind === 'texture') {
    const url = textureSrcOf(fv);
    return url ? { backgroundImage: `url(${url})`, backgroundSize: 'cover' } : null;
  }
  if (kind === 'gradient') return { backgroundImage: gradientCss(fv) };
  if (kind === 'solid') {
    const c = typeof fv === 'string' ? fv : fv.color;
    return { backgroundColor: colorOr(c, solidFallback) };
  }
  return null; // 'none' — PaintChip renders its slashed empty face
}

/** Best OPAQUE solid hex hiding inside any FillValue (chip fallbacks, seeds,
 * and every engine seam that cannot carry alpha — 3D faces included: alpha
 * is STRIPPED here, by law). */
export function solidHexFrom(fv, fallback = '#111827') {
  if (typeof fv === 'string') return normalizeOpaqueHex(fv, fallback);
  if (fv?.type === 'solid') return normalizeOpaqueHex(fv.color, fallback);
  const stops = fv?.stops ?? fv?.colorStops;
  if (stops?.length) return normalizeOpaqueHex(stops[0].color, fallback);
  if (fv?.from) return normalizeOpaqueHex(fv.from, fallback);
  return fallback;
}

/** Plain-words label for tooltips ("#dc2626" / "Circle gradient" / "Marble"). */
export function paintLabel(fv) {
  const kind = paintKind(fv);
  if (kind === 'solid') return typeof fv === 'string' ? fv : (fv.color ?? '');
  if (kind === 'gradient') return fv.type === 'radial-gradient' ? 'Circle gradient' : 'Gradient';
  if (kind === 'texture') {
    if (fv.src) return fv.label ?? 'Imported texture';
    return textureById(fv.id)?.label ?? fv.id;
  }
  return 'None';
}

// ---------------------------------------------------------------------------
// persisted lists — ONE tiny store pattern for recents / user swatches /
// user gradients / user textures (module cache + localStorage + pub/sub so
// every open picker refreshes the moment any surface writes).
function makeListStore(lsKey, cap) {
  let cache = null;
  const subs = new Set();
  const load = () => {
    if (cache) return cache;
    cache = [];
    try {
      const raw = window.localStorage?.getItem(lsKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) cache = parsed;
      }
    } catch { /* private mode etc. — session-only */ }
    return cache;
  };
  const save = () => {
    try { window.localStorage?.setItem(lsKey, JSON.stringify(cache)); } catch { /* quota — session-only */ }
    subs.forEach((fn) => fn());
  };
  return {
    list: () => load(),
    /** Prepend (dedupe by keyFn), cap. */
    push(entry, keyFn = JSON.stringify) {
      const k = keyFn(entry);
      cache = [entry, ...load().filter((e) => keyFn(e) !== k)].slice(0, cap);
      save();
    },
    remove(keyFn) {
      cache = load().filter((e) => !keyFn(e));
      save();
    },
    use() { // hook: re-render on any write
      const [, bump] = useState(0);
      useEffect(() => {
        const fn = () => bump((n) => n + 1);
        subs.add(fn);
        return () => subs.delete(fn);
      }, []);
      return load();
    },
  };
}

const userSwatchStore = makeListStore('pseUserSwatches.v1', 12);
const userGradientStore = makeListStore('pseUserGradients.v1', 8);
const userTextureStore = makeListStore('pseUserTextures.v1', 8);

// recent paints — v1 grouped shape preserved (pseRecentPaints.v1 is an
// OBJECT of group → ring), so saved rings survive the v2 upgrade.
const RECENT_MAX = 8;
const RECENT_LS_KEY = 'pseRecentPaints.v1';
let recentStore = null;
const recentSubs = new Set();

function loadRecents() {
  if (recentStore) return recentStore;
  recentStore = {};
  try {
    const raw = window.localStorage?.getItem(RECENT_LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') recentStore = parsed;
    }
  } catch { /* private mode etc. — session-only ring */ }
  return recentStore;
}

function recentsFor(group) {
  const list = loadRecents()[group];
  return Array.isArray(list) ? list : [];
}

/** Push a paint onto a group's ring (dedupe by value, cap 8, persist). */
export function rememberPaint(group, fv) {
  if (fv == null) return;
  const entry = typeof fv === 'string'
    ? fv.toLowerCase()
    : fv.type === 'solid' ? (fv.color ?? '').toLowerCase() : fv;
  if (typeof entry === 'string' && !parseColor(entry)) return;
  const store = loadRecents();
  const key = JSON.stringify(entry);
  const next = [entry, ...recentsFor(group).filter((e) => JSON.stringify(e) !== key)]
    .slice(0, RECENT_MAX);
  store[group] = next;
  try { window.localStorage?.setItem(RECENT_LS_KEY, JSON.stringify(store)); } catch { /* ok */ }
  recentSubs.forEach((fn) => fn());
}

// (useRecentPaints retired 2026-07-23 — the pane-local pseRecentPaints.v1 read
// moved to LibraryShelf's colorLibrary store; rememberPaint still writes the v1
// ring below so any external reader stays fed, but nothing subscribes here.)

/** Right-click OR long-press (600ms) → onRemove. The removal grammar for
 * every user-saved chip (swatches, gradient presets, textures). A hold that
 * fires swallows the click that follows it, so removing never also picks. */
function removableProps(onRemove) {
  let timer = null;
  let fired = false;
  return {
    onContextMenu: (e) => { e.preventDefault(); onRemove(); },
    onPointerDown: () => { fired = false; timer = setTimeout(() => { fired = true; onRemove(); }, 600); },
    onPointerUp: () => clearTimeout(timer),
    onPointerLeave: () => clearTimeout(timer),
    onPointerMove: () => clearTimeout(timer),
    onClickCapture: (e) => { if (fired) { e.preventDefault(); e.stopPropagation(); } },
  };
}

// ---------------------------------------------------------------------------
// picker UI prefs — accordion open-states, persisted so a maker's choice to
// expand "Color code" or "My galleries" survives a reopen (owner 2026-07-23:
// both fold COLLAPSED by default — the codes + galleries are the long tail the
// default view no longer shows). Module cache + localStorage + pub/sub so every
// open picker toggles in step. Key: pseColorPickerUi.v1.
const PICKER_UI_KEY = 'pseColorPickerUi.v1';
let pickerUiCache = null;
const pickerUiSubs = new Set();
function loadPickerUi() {
  if (pickerUiCache) return pickerUiCache;
  pickerUiCache = {};
  try {
    const raw = window.localStorage?.getItem(PICKER_UI_KEY);
    if (raw) { const p = JSON.parse(raw); if (p && typeof p === 'object') pickerUiCache = p; }
  } catch { /* private mode — session-only */ }
  return pickerUiCache;
}
function setPickerUiFlag(key, val) {
  const ui = loadPickerUi();
  ui[key] = !!val;
  try { window.localStorage?.setItem(PICKER_UI_KEY, JSON.stringify(ui)); } catch { /* quota — session-only */ }
  pickerUiSubs.forEach((fn) => fn());
}
/** [open, setOpen] for one persisted accordion flag (collapsed unless stored). */
function usePickerUiFlag(key, dflt = false) {
  const [, bump] = useState(0);
  useEffect(() => {
    const fn = () => bump((n) => n + 1);
    pickerUiSubs.add(fn);
    return () => pickerUiSubs.delete(fn);
  }, []);
  const ui = loadPickerUi();
  const open = key in ui ? !!ui[key] : dflt;
  return [open, (v) => setPickerUiFlag(key, v)];
}

/** A slim disclosure in the drawer's accordion idiom (11px uppercase header,
 * chevron, hairline). Collapsed by default; the body only MOUNTS while open, so
 * the collapsed content stays out of tab order (and out of a probe's reach until
 * the header is clicked). data-acc is the stable hook. */
function Accordion({ id, title, defaultOpen = false, children }) {
  const [open, setOpen] = usePickerUiFlag(`acc.${id}`, defaultOpen);
  return (
    <div className={`pp-acc${open ? ' pp-acc-open' : ''}`} data-acc={id}>
      <button
        type="button"
        className="pp-acc-head"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="pp-acc-title">{title}</span>
        <span className="pp-acc-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>
      {open && <div className="pp-acc-body">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PaintChip — the truthful face. A button when it does something (onClick),
// a plain span when it only displays.
export function PaintChip({
  value, title, onClick, className = '', active = false, refEl,
  ariaExpanded, ariaHaspopup,
}) {
  const kind = paintKind(value);
  const style = paintCss(value) ?? undefined;
  const cls = `pp-chip${kind === 'none' ? ' pp-chip-none' : ''}${active ? ' active' : ''}${className ? ` ${className}` : ''}`;
  if (typeof onClick !== 'function') {
    return <span className={cls} style={style} data-paint-kind={kind} title={title} aria-hidden={title ? undefined : 'true'} />;
  }
  return (
    <button
      type="button"
      ref={refEl}
      className={cls}
      style={style}
      data-paint-kind={kind}
      title={title}
      aria-label={title}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
      onClick={onClick}
    />
  );
}

// ---------------------------------------------------------------------------
// small shared bits

/** Section header in the mock's small-caps grammar, with an optional action
 * button docked right (the SWATCHES "+", the PRESETS gear). */
function PpHead({ children, action }) {
  return (
    <div className="pp-head">
      <span className="pp-minihead">{children}</span>
      {action}
    </div>
  );
}

/** House slider row with the drag law wired and the mock's right-aligned
 * readout ("270°", "100%"). */
function PpSlider({ label, readout, value, min, max, step, onChange, onEnd, sliderId }) {
  return (
    <div className="pp-slider-row" data-slider-id={sliderId}>
      <div className="pp-slider-head">
        <span className="pp-slider-label">{label}</span>
        {readout != null && <span className="pp-slider-readout">{readout}</span>}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        onPointerUp={onEnd}
        onBlur={onEnd}
      />
    </div>
  );
}

function PpNumField({ label, short, value, onDraft, onCommit, max }) {
  return (
    <label className="pp-num-cell" title={label}>
      {short && <span aria-hidden="true">{short}</span>}
      <input
        type="number"
        min={0}
        max={max}
        value={value}
        aria-label={label}
        onChange={(e) => onDraft(e.target.value)}
        onBlur={onCommit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onCommit(); } }}
      />
    </label>
  );
}

/** Copy-to-clipboard with the execCommand fallback. */
function copyText(text) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => {});
    return;
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    ta.remove();
  } catch { /* ok — quiet */ }
}

// ---------------------------------------------------------------------------
// SolidEditor — the picker mechanic: LARGE 2D saturation/value pad + hue bar
// (with degree readout) + OPACITY bar (paint alpha, checkerboard) — all
// pointer-captured, keyboard-nudgeable, no deps. Reused verbatim inside the
// Gradient pane for stop colors (one mechanic everywhere; per-stop alpha
// rides the same Opacity bar).
//
// Hue survives gray AND alpha survives re-syncs: {h,s,v,a} is LOCAL state,
// re-synced from `color` only when it stops matching what this editor last
// emitted (external change).
function SolidEditor({ color, onLive, onPick, onEnd, opaque = false, showHex = false }) {
  const safe = colorOr(color, '#111827');
  const [hsva, setHsva] = useState(() => {
    const c = parseColor(safe);
    return { ...rgbToHsv(c), a: opaque ? 1 : c.a };
  });
  const lastOwn = useRef(safe.toLowerCase());
  const [hexDraft, setHexDraft] = useState(null);

  useEffect(() => {
    if (safe.toLowerCase() !== lastOwn.current) {
      const c = parseColor(safe);
      setHsva({ ...rgbToHsv(c), a: opaque ? 1 : c.a });
      lastOwn.current = safe.toLowerCase();
    }
  }, [safe, opaque]);

  const toStr = (v) => formatColor({ ...hsvToRgb(v), a: opaque ? 1 : v.a });
  const emit = (next, { live = false } = {}) => {
    hsvaRef.current = next; // pointer streams outpace renders
    setHsva(next);
    const out = toStr(next);
    lastOwn.current = out.toLowerCase();
    if (live) onLive(out);
    else onPick(out);
  };

  // pointer handlers read the freshest hsva through a ref (capture-drag)
  const hsvaRef = useRef(hsva);
  hsvaRef.current = hsva;

  const svRef = useRef(null);
  const svDrag = useRef(false);
  const svFrom = (e) => {
    const r = svRef.current.getBoundingClientRect();
    const s = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const v = Math.min(1, Math.max(0, 1 - (e.clientY - r.top) / r.height));
    emit({ ...hsvaRef.current, s, v }, { live: true });
  };

  const hueRef = useRef(null);
  const hueDrag = useRef(false);
  const hueFrom = (e) => {
    const r = hueRef.current.getBoundingClientRect();
    const h = Math.min(360, Math.max(0, ((e.clientX - r.left) / r.width) * 360));
    emit({ ...hsvaRef.current, h }, { live: true });
  };

  const alphaRef = useRef(null);
  const alphaDrag = useRef(false);
  const alphaFrom = (e) => {
    const r = alphaRef.current.getBoundingClientRect();
    const a = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    emit({ ...hsvaRef.current, a }, { live: true });
  };

  const pad = (dragRef, compute) => ({
    onPointerDown: (e) => {
      dragRef.current = true;
      e.currentTarget.setPointerCapture?.(e.pointerId);
      compute(e);
    },
    onPointerMove: (e) => { if (dragRef.current) compute(e); },
    onPointerUp: (e) => {
      if (!dragRef.current) return;
      dragRef.current = false;
      try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ok */ }
      onEnd();
    },
    onPointerCancel: () => { dragRef.current = false; onEnd(); },
  });

  const commitHexDraft = () => {
    const parsed = parseHexInput(hexDraft ?? '');
    setHexDraft(null);
    if (parsed) {
      const c = parseColor(parsed);
      const next = { ...rgbToHsv(c), a: opaque ? 1 : c.a };
      setHsva(next);
      const out = toStr(next);
      lastOwn.current = out.toLowerCase();
      onPick(out);
    }
  };

  const hueCss = `hsl(${Math.round(hsva.h)}, 100%, 50%)`;
  const rgbNow = hsvToRgb(hsva);
  const opaqueNow = `rgb(${rgbNow.r}, ${rgbNow.g}, ${rgbNow.b})`;
  return (
    <div className="pp-solid-editor">
      <div
        ref={svRef}
        className="pp-sv"
        role="slider"
        aria-label="Shade — arrows adjust, drag to mix"
        aria-valuetext={`saturation ${Math.round(hsva.s * 100)}%, brightness ${Math.round(hsva.v * 100)}%`}
        tabIndex={0}
        style={{ backgroundColor: hueCss }}
        {...pad(svDrag, svFrom)}
        onKeyDown={(e) => {
          const step = 0.03;
          if (e.key === 'ArrowLeft') { e.preventDefault(); emit({ ...hsva, s: Math.max(0, hsva.s - step) }); }
          else if (e.key === 'ArrowRight') { e.preventDefault(); emit({ ...hsva, s: Math.min(1, hsva.s + step) }); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); emit({ ...hsva, v: Math.min(1, hsva.v + step) }); }
          else if (e.key === 'ArrowDown') { e.preventDefault(); emit({ ...hsva, v: Math.max(0, hsva.v - step) }); }
        }}
      >
        <span
          className="pp-sv-dot"
          style={{ left: `${hsva.s * 100}%`, top: `${(1 - hsva.v) * 100}%`, backgroundColor: opaqueNow }}
          aria-hidden="true"
        />
      </div>
      <div className="pp-bar-row" data-slider-id="hue">
        <div
          ref={hueRef}
          className="pp-hue"
          role="slider"
          aria-label="Hue"
          aria-valuemin={0}
          aria-valuemax={360}
          aria-valuenow={Math.round(hsva.h)}
          tabIndex={0}
          {...pad(hueDrag, hueFrom)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); emit({ ...hsva, h: (hsva.h + 355) % 360 }); }
            else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); emit({ ...hsva, h: (hsva.h + 5) % 360 }); }
          }}
        >
          <span className="pp-bar-dot" style={{ left: `${(hsva.h / 360) * 100}%` }} aria-hidden="true" />
        </div>
        <span className="pp-bar-readout">{Math.round(hsva.h)}°</span>
      </div>
      {!opaque && (
        <div className="pp-bar-row" data-slider-id="paintAlpha">
          <div
            ref={alphaRef}
            className="pp-alpha"
            role="slider"
            aria-label="Opacity"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(hsva.a * 100)}
            tabIndex={0}
            {...pad(alphaDrag, alphaFrom)}
            onKeyDown={(e) => {
              const step = 0.05;
              if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); emit({ ...hsva, a: Math.max(0, hsva.a - step) }); }
              else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); emit({ ...hsva, a: Math.min(1, hsva.a + step) }); }
            }}
          >
            <span
              className="pp-alpha-ramp"
              style={{ backgroundImage: `linear-gradient(to right, transparent, ${opaqueNow})` }}
              aria-hidden="true"
            />
            <span className="pp-bar-dot" style={{ left: `${hsva.a * 100}%` }} aria-hidden="true" />
          </div>
          <span className="pp-bar-readout">{Math.round(hsva.a * 100)}%</span>
        </div>
      )}
      {showHex && (
        <div className="pp-hex-row">
          <span className="pp-chip pp-hex-swatch" style={{ backgroundColor: safe }} aria-hidden="true" />
          <input
            type="text"
            className="pp-hex-input"
            value={hexDraft !== null ? hexDraft : safe}
            aria-label="Hex color"
            spellCheck={false}
            onChange={(e) => setHexDraft(e.target.value)}
            onBlur={commitHexDraft}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitHexDraft(); } }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// value rows — [FMT ▾][fields][copy], two independently-switchable rows
// (mock: HEX + RGB by default; the set is HEX/RGB/CMYK).
const FMTS = ['hex', 'rgb', 'cmyk'];

function ValueRow({ rowId, fmt, setFmt, colorStr, onPickColor }) {
  const c = parseColor(colorStr) ?? { r: 17, g: 24, b: 39, a: 1 };
  const cmyk = rgbToCmyk(c);
  const [drafts, setDrafts] = useState({});
  const draft = (k, live) => (drafts[k] !== undefined ? drafts[k] : String(live));
  const setDraft = (k) => (v) => setDrafts((d) => ({ ...d, [k]: v }));
  const hexShown = c.a >= 0.999
    ? rgbToHex(c)
    : `${rgbToHex(c)}${Math.round(c.a * 255).toString(16).padStart(2, '0')}`;

  const commitHex = () => {
    const parsed = parseHexInput(drafts.hex ?? '');
    setDrafts({});
    if (parsed) onPickColor(formatColor(parseColor(parsed)));
  };
  const commitRgb = () => {
    const next = {
      r: parseInt(draft('r', c.r), 10),
      g: parseInt(draft('g', c.g), 10),
      b: parseInt(draft('b', c.b), 10),
    };
    setDrafts({});
    if ([next.r, next.g, next.b].some(Number.isNaN)) return;
    onPickColor(formatColor({ ...next, a: c.a }));
  };
  const commitCmyk = () => {
    const next = {
      c: parseInt(draft('c', cmyk.c), 10),
      m: parseInt(draft('m', cmyk.m), 10),
      y: parseInt(draft('y', cmyk.y), 10),
      k: parseInt(draft('k', cmyk.k), 10),
    };
    setDrafts({});
    if ([next.c, next.m, next.y, next.k].some(Number.isNaN)) return;
    onPickColor(formatColor({ ...cmykToRgb(next), a: c.a }));
  };

  const copyValue = fmt === 'hex' ? hexShown
    : fmt === 'rgb' ? (c.a >= 0.999 ? `rgb(${c.r}, ${c.g}, ${c.b})` : formatColor(c))
      : `${cmyk.c}, ${cmyk.m}, ${cmyk.y}, ${cmyk.k}`;

  return (
    <div className="pp-value-row" data-value-row={rowId} data-fmt={fmt}>
      <select
        className="pp-fmt"
        value={fmt}
        aria-label="Value format"
        onChange={(e) => { setDrafts({}); setFmt(e.target.value); }}
      >
        {FMTS.map((f) => <option key={f} value={f}>{f.toUpperCase()}</option>)}
      </select>
      {fmt === 'hex' && (
        <input
          type="text"
          className="pp-hex-input"
          value={drafts.hex !== undefined ? drafts.hex : hexShown}
          aria-label="Hex color"
          spellCheck={false}
          onChange={(e) => setDraft('hex')(e.target.value)}
          onBlur={commitHex}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitHex(); } }}
        />
      )}
      {fmt === 'rgb' && (
        <span className="pp-num-set" role="group" aria-label="RGB values">
          <PpNumField label="Red" max={255} value={draft('r', c.r)} onDraft={setDraft('r')} onCommit={commitRgb} />
          <PpNumField label="Green" max={255} value={draft('g', c.g)} onDraft={setDraft('g')} onCommit={commitRgb} />
          <PpNumField label="Blue" max={255} value={draft('b', c.b)} onDraft={setDraft('b')} onCommit={commitRgb} />
        </span>
      )}
      {fmt === 'cmyk' && (
        <span className="pp-num-set" role="group" aria-label="CMYK values">
          <PpNumField label="Cyan" max={100} value={draft('c', cmyk.c)} onDraft={setDraft('c')} onCommit={commitCmyk} />
          <PpNumField label="Magenta" max={100} value={draft('m', cmyk.m)} onDraft={setDraft('m')} onCommit={commitCmyk} />
          <PpNumField label="Yellow" max={100} value={draft('y', cmyk.y)} onDraft={setDraft('y')} onCommit={commitCmyk} />
          <PpNumField label="Black ink" max={100} value={draft('k', cmyk.k)} onDraft={setDraft('k')} onCommit={commitCmyk} />
        </span>
      )}
      <button
        type="button"
        className="pp-copy"
        title={`Copy ${fmt.toUpperCase()} value`}
        aria-label={`Copy ${fmt.toUpperCase()} value`}
        onClick={() => copyText(copyValue)}
      >
        ⧉
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// COLOR pane — SWATCHES card (+ saves a custom swatch) → editor (SV/hue/
// opacity) → dual value rows. Recents ring stays (distinct from swatches).
function SolidPane({ value, emit, endStroke, palette, opaque, suggestSlot }) {
  const curStr = typeof value === 'string' ? colorOr(value, '#111827')
    : value?.type === 'solid' ? colorOr(value.color, '#111827')
      : solidHexFrom(value);
  const isSolid = paintKind(value) === 'solid';
  const [fmt1, setFmt1] = useState('hex');
  const [fmt2, setFmt2] = useState('rgb');
  const userSwatches = userSwatchStore.use();

  const curKey = normalizeOpaqueHex(curStr).toLowerCase();

  return (
    <div className="pp-body" data-color-pane="solid">
      <div className="pp-card">
        <PpHead
          action={(
            <button
              type="button"
              className="pp-head-btn pp-swatch-add"
              title="Add the current color"
              aria-label="Add the current color"
              onClick={() => userSwatchStore.push(curStr.toLowerCase(), (s) => s)}
            >
              +
            </button>
          )}
        >
          Standard colors
        </PpHead>
        <div className="pp-grid" role="group" aria-label="Color presets">
          {palette.map((c) => (
            <button
              key={c}
              type="button"
              className={`pp-swatch${isSolid && curKey === c.toLowerCase() ? ' active' : ''}`}
              style={{ backgroundColor: c }}
              title={c}
              aria-label={`Color ${c}`}
              data-swatch={c}
              onClick={() => emit(c)}
            />
          ))}
          {userSwatches.map((c) => (
            <button
              key={`u-${c}`}
              type="button"
              className={`pp-swatch pp-user-swatch${isSolid && curStr.toLowerCase() === c ? ' active' : ''}`}
              style={{ backgroundColor: c }}
              title={`${c} — right-click or hold to remove`}
              aria-label={`Saved color ${c}`}
              data-swatch={c}
              onClick={() => emit(c)}
              {...removableProps(() => userSwatchStore.remove((e) => e === c))}
            />
          ))}
        </div>
        {/* Pane-local Recent row retired 2026-07-21 — the LibraryShelf renders
            the ONE global per-kind Recent (rememberPaint still writes the
            per-host store; only the render moved). */}
      </div>
      {/* "From this poster" + "Goes well with" ride BETWEEN the swatches and the
          SV pad (owner 2026-07-23 order: Recent → Standard colors → Goes well
          with → picker). */}
      {suggestSlot}
      <SolidEditor
        color={curStr}
        opaque={opaque}
        onLive={(c) => emit(c, { live: true })}
        onPick={(c) => emit(c)}
        onEnd={endStroke}
      />
      {/* HEX / RGB / CMYK fold into a collapsed-by-default accordion — the SV pad
          is the primary surface; the numeric codes are the long tail. */}
      <Accordion id="code" title="Color code">
        <div className="pp-values">
          <ValueRow rowId="1" fmt={fmt1} setFmt={setFmt1} colorStr={curStr} onPickColor={(c) => emit(c)} />
          <ValueRow rowId="2" fmt={fmt2} setFmt={setFmt2} colorStr={curStr} onPickColor={(c) => emit(c)} />
        </div>
      </Accordion>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GRADIENT pane — the granularity tab: PREVIEW ramp, the STOPS TRACK (square
// stop handles + diamond midpoint markers), TYPE thumbs, ANGLE dial +
// stepper, PRESETS (house 2×4 + user, gear saves), "+ New Gradient".
//
// LOCAL MODEL while the pane lives: { stops:[{id,offset,color}], biases:
// {leftStopId: 0..1}, type, angle }. Emissions BAKE biases into plain stops
// (see header). Resync from `value` only when it stops matching the last
// emission (external change) — biases reset then, honestly: baked stops ARE
// the truth outside this pane.
let stopUid = 0;
const newStopId = () => `s${(stopUid += 1)}`;

function logicalFromValue(fv) {
  return paintStops(fv).map((s) => ({ id: newStopId(), offset: s.offset ?? 0, color: s.color }));
}

function bakeStops(stops, biases) {
  const sorted = [...stops].sort((a, b) => a.offset - b.offset);
  const out = [];
  sorted.forEach((s, i) => {
    out.push({ offset: Math.round(s.offset * 1000) / 1000, color: s.color });
    const next = sorted[i + 1];
    if (!next) return;
    const bias = biases[s.id];
    if (bias == null || Math.abs(bias - 0.5) < 0.02) return;
    const off = s.offset + (next.offset - s.offset) * bias;
    out.push({
      offset: Math.round(off * 1000) / 1000,
      color: mixColors(s.color, next.color, 0.5),
    });
  });
  return out;
}

/** Gradient color at offset t over the logical stops (for track-click adds). */
function colorAt(stops, t) {
  const sorted = [...stops].sort((a, b) => a.offset - b.offset);
  if (t <= sorted[0].offset) return sorted[0].color;
  for (let i = 0; i < sorted.length - 1; i += 1) {
    const a = sorted[i];
    const b = sorted[i + 1];
    if (t <= b.offset) {
      const span = b.offset - a.offset || 1;
      return mixColors(a.color, b.color, (t - a.offset) / span);
    }
  }
  return sorted[sorted.length - 1].color;
}

const gradKey = (fv) => JSON.stringify(fv ?? null);

/** Preset ring truth: the live gradient IS this preset (type+angle+stops). */
const presetActive = (fv, p) => paintKind(fv) === 'gradient'
  && fv.type === p.fill.type
  && (fv.angle ?? 0) === (p.fill.angle ?? 0)
  && JSON.stringify(fv.stops) === JSON.stringify(p.fill.stops);

function GradientPane({ value, emit, endStroke, linearOnly, opaque }) {
  const isGradient = paintKind(value) === 'gradient';
  const [grad, setGrad] = useState(() => (isGradient ? {
    stops: logicalFromValue(value),
    biases: {},
    type: value.type,
    angle: value.angle ?? 0,
  } : null));
  const [selId, setSelId] = useState(() => grad?.stops[0]?.id ?? null);
  const lastOwn = useRef(isGradient ? gradKey(value) : null);
  const gradRef = useRef(grad);
  gradRef.current = grad;

  // external change (selection swap, host writes, Clear) → resync
  useEffect(() => {
    const k = isGradient ? gradKey(value) : null;
    if (k === lastOwn.current) return;
    lastOwn.current = k;
    if (!isGradient) { setGrad(null); setSelId(null); return; }
    const stops = logicalFromValue(value);
    setGrad({ stops, biases: {}, type: value.type, angle: value.angle ?? 0 });
    setSelId(stops[0]?.id ?? null);
  }, [isGradient, value]);

  const userPresets = userGradientStore.use();

  const write = (next, meta) => {
    const fill = { type: next.type, stops: bakeStops(next.stops, next.biases) };
    if (next.type === 'linear-gradient') fill.angle = next.angle; // radial drops angle
    lastOwn.current = gradKey(fill);
    gradRef.current = next; // pointer streams outpace renders — keep the ref hot
    setGrad(next);
    emit(fill, meta);
  };

  const sorted = grad ? [...grad.stops].sort((a, b) => a.offset - b.offset) : [];
  const sel = sorted.find((s) => s.id === selId) ?? sorted[0] ?? null;
  const isRadial = grad?.type === 'radial-gradient';
  const trackCss = grad
    ? `linear-gradient(90deg, ${bakeStops(grad.stops, grad.biases).map((s) => `${s.color} ${s.offset * 100}%`).join(', ')})`
    : null;

  // ---- stops track pointer machinery ----
  const trackRef = useRef(null);
  const dragStop = useRef(null); // { id, kind:'stop'|'mid', off:boolean }
  const offAt = (e) => {
    const r = trackRef.current.getBoundingClientRect();
    return Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
  };
  const moveStop = (id, off) => {
    const g = gradRef.current;
    write({ ...g, stops: g.stops.map((s) => (s.id === id ? { ...s, offset: off } : s)) }, { live: true });
  };
  const moveMid = (leftId, e) => {
    const g = gradRef.current;
    const srt = [...g.stops].sort((a, b) => a.offset - b.offset);
    const i = srt.findIndex((s) => s.id === leftId);
    const a = srt[i];
    const b = srt[i + 1];
    if (!a || !b) return;
    const t = offAt(e);
    const span = b.offset - a.offset || 1;
    const bias = Math.min(0.95, Math.max(0.05, (t - a.offset) / span));
    write({ ...g, biases: { ...g.biases, [leftId]: bias } }, { live: true });
  };
  const addStopAt = (off) => {
    const g = gradRef.current;
    const stop = { id: newStopId(), offset: off, color: colorAt(g.stops, off) };
    write({ ...g, stops: [...g.stops, stop] });
    setSelId(stop.id);
  };
  const removeStop = (id) => {
    const g = gradRef.current;
    if (g.stops.length <= 2) return; // 2-stop floor
    const left = [...g.stops].sort((a, b) => a.offset - b.offset)
      .findIndex((s) => s.id === id);
    const nextStops = g.stops.filter((s) => s.id !== id);
    const { [id]: _gone, ...biases } = g.biases;
    write({ ...g, stops: nextStops, biases });
    if (selId === id) setSelId(nextStops[Math.max(0, left - 1)]?.id ?? nextStops[0].id);
  };

  const trackHandlers = {
    onPointerDown: (e) => {
      const el = e.target.closest?.('[data-stop-id],[data-mid-id]');
      e.currentTarget.setPointerCapture?.(e.pointerId);
      if (el?.dataset.stopId) {
        dragStop.current = { id: el.dataset.stopId, kind: 'stop', off: false };
        setSelId(el.dataset.stopId);
      } else if (el?.dataset.midId) {
        dragStop.current = { id: el.dataset.midId, kind: 'mid' };
      } else {
        dragStop.current = null;
        addStopAt(offAt(e)); // click empty track = add stop
      }
    },
    onPointerMove: (e) => {
      const d = dragStop.current;
      if (!d) return;
      if (d.kind === 'stop') {
        const r = trackRef.current.getBoundingClientRect();
        d.off = e.clientY > r.bottom + 36 && gradRef.current.stops.length > 2;
        moveStop(d.id, offAt(e));
      } else moveMid(d.id, e);
    },
    onPointerUp: (e) => {
      const d = dragStop.current;
      dragStop.current = null;
      try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ok */ }
      if (d?.kind === 'stop' && d.off) removeStop(d.id); // drag-off delete
      if (d) endStroke();
    },
    onPointerCancel: () => { dragStop.current = null; endStroke(); },
    onKeyDown: (e) => {
      if (!sel) return;
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeStop(sel.id); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); moveStop(sel.id, Math.max(0, sel.offset - 0.01)); endStroke(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); moveStop(sel.id, Math.min(1, sel.offset + 0.01)); endStroke(); }
    },
  };

  const setType = (type) => {
    const g = gradRef.current;
    if (!g || g.type === type) return;
    write({ ...g, type, angle: g.angle ?? 45 });
  };
  const setAngle = (deg, meta) => {
    const g = gradRef.current;
    const a = ((Math.round(deg) % 360) + 360) % 360;
    write({ ...g, angle: a }, meta);
  };

  // angle dial drag
  const dialRef = useRef(null);
  const dialDrag = useRef(false);
  const dialFrom = (e) => {
    const r = dialRef.current.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2);
    const dy = e.clientY - (r.top + r.height / 2);
    // house grammar: 0° = left→right, 90° = top→bottom (see gradientCss)
    setAngle((Math.atan2(dy, dx) * 180) / Math.PI, { live: true });
  };

  const startFresh = () => {
    const seed = solidHexFrom(value, '#7c3aed');
    const partner = mixColors(seed, parseColor(seed) && rgbToHsv(parseColor(seed)).v > 0.75 ? '#000000' : '#ffffff', 0.8);
    emit({ type: 'linear-gradient', angle: 90, stops: [{ offset: 0, color: seed }, { offset: 1, color: partner }] });
  };

  const presetsBlock = (
    <div className="pp-card">
      <PpHead
        action={(
          <button
            type="button"
            className="pp-head-btn pp-preset-gear"
            title={isGradient ? 'Save this gradient as a preset' : 'Make a gradient first, then save it here'}
            aria-label="Save this gradient as a preset"
            disabled={!isGradient}
            onClick={() => {
              if (!isGradient || !grad) return;
              const fill = { type: grad.type, stops: bakeStops(grad.stops, grad.biases) };
              if (grad.type === 'linear-gradient') fill.angle = grad.angle;
              userGradientStore.push({ id: `ug-${Date.now().toString(36)}`, fill }, (p) => JSON.stringify(p.fill));
            }}
          >
            ⚙
          </button>
        )}
      >
        Presets
      </PpHead>
      <div className="pp-grid pp-grad-grid" role="group" aria-label="Gradient presets">
        {GRADIENT_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`pp-swatch pp-grad-swatch${presetActive(value, p) ? ' active' : ''}`}
            style={{ backgroundImage: gradientCss(p.fill) }}
            title={p.label}
            aria-label={`Gradient ${p.label}`}
            data-gradient-id={p.id}
            onClick={() => emit(p.fill)}
          />
        ))}
        {userPresets.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`pp-swatch pp-grad-swatch pp-user-grad${presetActive(value, p) ? ' active' : ''}`}
            style={{ backgroundImage: gradientCss(p.fill) }}
            title="Saved gradient — right-click or hold to remove"
            aria-label="Saved gradient"
            data-gradient-id={p.id}
            onClick={() => emit(p.fill)}
            {...removableProps(() => userGradientStore.remove((e) => e.id === p.id))}
          />
        ))}
      </div>
    </div>
  );

  if (!isGradient || !grad) {
    return (
      <div className="pp-body" data-color-pane="gradient">
        {presetsBlock}
        <div className="pp-hint">Pick a ramp — or start one from your current color — then tune every stop here.</div>
        <button type="button" className="pp-new-grad" onClick={startFresh}>+ New Gradient</button>
      </div>
    );
  }

  return (
    <div className="pp-body" data-color-pane="gradient">
      <div className="pp-card">
        <PpHead>Preview</PpHead>
        <div className="pp-grad-preview" style={{ backgroundImage: gradientCss({ ...grad, stops: bakeStops(grad.stops, grad.biases), type: grad.type, angle: grad.angle }) }} aria-hidden="true" />
        <div
          ref={trackRef}
          className="pp-stops-track"
          role="group"
          aria-label="Gradient stops — click adds, drag moves, drag away removes; diamonds bend the blend"
          tabIndex={0}
          {...trackHandlers}
        >
          <div className="pp-stops-ramp" style={{ backgroundImage: trackCss }} aria-hidden="true" />
          {sorted.map((s, i) => {
            const next = sorted[i + 1];
            const bias = grad.biases[s.id] ?? 0.5;
            return (
              <React.Fragment key={s.id}>
                <span
                  className={`pp-stop pp-stop-handle${sel?.id === s.id ? ' selected' : ''}`}
                  data-stop-id={s.id}
                  style={{ left: `${s.offset * 100}%`, backgroundColor: s.color }}
                  title={`Stop at ${Math.round(s.offset * 100)}%`}
                />
                {next && (
                  <span
                    className="pp-mid"
                    data-mid-id={s.id}
                    style={{ left: `${(s.offset + (next.offset - s.offset) * bias) * 100}%` }}
                    title="Blend midpoint"
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <div className="pp-stops-meta">
          <span>{sorted.length} stops{sel ? ` · selected ${Math.round(sel.offset * 100)}%` : ''}</span>
          <button
            type="button"
            className="pp-stop-del"
            disabled={sorted.length <= 2}
            title={sorted.length <= 2 ? 'Gradients keep at least two stops' : 'Remove the selected stop'}
            onClick={() => sel && removeStop(sel.id)}
          >
            × Remove stop
          </button>
        </div>
      </div>

      <div className="pp-grad-controls">
        <div className="pp-card pp-type-card">
          <PpHead>Type</PpHead>
          <div className="pp-type-row" role="group" aria-label="Gradient type">
            <button
              type="button"
              className={`pp-type-thumb pp-type-linear${!isRadial ? ' active' : ''}`}
              data-grad-type="linear"
              title="Linear"
              aria-label="Linear gradient"
              onClick={() => setType('linear-gradient')}
            />
            {!linearOnly && (
              <button
                type="button"
                className={`pp-type-thumb pp-type-radial${isRadial ? ' active' : ''}`}
                data-grad-type="radial"
                title="Radial"
                aria-label="Radial gradient"
                onClick={() => setType('radial-gradient')}
              />
            )}
          </div>
        </div>
        <div className={`pp-card pp-angle-card${isRadial ? ' pp-disabled' : ''}`}>
          <PpHead>Angle</PpHead>
          <div className="pp-angle-row">
            <div
              ref={dialRef}
              className="pp-angle-dial"
              role="slider"
              aria-label="Gradient angle"
              aria-valuemin={0}
              aria-valuemax={360}
              aria-valuenow={grad.angle}
              aria-disabled={isRadial || undefined}
              tabIndex={isRadial ? -1 : 0}
              onPointerDown={(e) => {
                if (isRadial) return;
                dialDrag.current = true;
                e.currentTarget.setPointerCapture?.(e.pointerId);
                dialFrom(e);
              }}
              onPointerMove={(e) => { if (dialDrag.current) dialFrom(e); }}
              onPointerUp={(e) => {
                if (!dialDrag.current) return;
                dialDrag.current = false;
                try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ok */ }
                endStroke();
              }}
              onPointerCancel={() => { dialDrag.current = false; endStroke(); }}
              onKeyDown={(e) => {
                if (isRadial) return;
                if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') { e.preventDefault(); setAngle(grad.angle - 5); endStroke(); }
                else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') { e.preventDefault(); setAngle(grad.angle + 5); endStroke(); }
              }}
            >
              <span className="pp-angle-needle" style={{ transform: `rotate(${grad.angle}deg)` }} aria-hidden="true" />
            </div>
            <input
              type="number"
              className="pp-angle-num"
              min={0}
              max={360}
              step={1}
              value={grad.angle}
              disabled={isRadial}
              aria-label="Gradient angle in degrees"
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!Number.isNaN(v)) setAngle(v, { live: true });
              }}
              onBlur={endStroke}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); endStroke(); } }}
            />
          </div>
        </div>
      </div>

      {sel && (
        <div className="pp-card">
          <PpHead>Stop color</PpHead>
          <SolidEditor
            color={colorOr(sel.color, '#000000')}
            opaque={opaque}
            showHex
            onLive={(c) => write({ ...grad, stops: grad.stops.map((s) => (s.id === sel.id ? { ...s, color: c } : s)) }, { live: true })}
            onPick={(c) => write({ ...grad, stops: grad.stops.map((s) => (s.id === sel.id ? { ...s, color: c } : s)) })}
            onEnd={endStroke}
          />
        </div>
      )}

      {presetsBlock}
      <button type="button" className="pp-new-grad" onClick={startFresh}>+ New Gradient</button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TEXTURE pane — labeled cards (manifest, category chips kept for the full
// library), user-texture row, scale slider, Import Texture… (Media-panel
// pipeline: FileReader → data URL, downscaled), Browse-all escape hatch.

/** file → downscaled (≤1024px) data URL. WebP where the runtime encodes it,
 * PNG otherwise (canvas.toDataURL falls back on its own). */
function fileToTextureDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('decode failed'));
      img.onload = () => {
        const max = 1024;
        const k = Math.min(1, max / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
        const w = Math.max(1, Math.round((img.naturalWidth || 1) * k));
        const h = Math.max(1, Math.round((img.naturalHeight || 1) * k));
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(c.toDataURL('image/webp', 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function TexturePane({ value, emit, endStroke, onBrowseAll, allowImport }) {
  const isTex = value?.type === 'texture';
  const activeId = isTex ? (value.id ?? null) : null;
  const activeSrc = isTex ? (value.src ?? null) : null;
  const scale = isTex ? (value.scale ?? 1) : 1;
  const [cat, setCat] = useState('featured');
  const fileRef = useRef(null);
  const userTextures = userTextureStore.use();

  const shownBase = useMemo(() => (
    cat === 'featured' ? TEXTURES_FEATURED : TEXTURES.filter((t) => t.category === cat)
  ), [cat]);
  const activeEntry = activeId ? (textureById(activeId) ?? { id: activeId, label: activeId }) : null;
  const shown = activeEntry && !shownBase.some((t) => t.id === activeEntry.id)
    ? [activeEntry, ...shownBase]
    : shownBase;

  const importFile = async (file) => {
    if (!file || !file.type?.startsWith('image/')) return;
    try {
      const src = await fileToTextureDataUrl(file);
      const label = (file.name || 'Imported').replace(/\.[a-z0-9]+$/i, '').slice(0, 24) || 'Imported';
      userTextureStore.push({ id: `user-${Date.now().toString(36)}`, label, src }, (t) => t.src);
      emit({ type: 'texture', src, scale: 1 });
    } catch { /* unreadable file — quiet, nothing applied */ }
  };

  return (
    <div className="pp-body" data-color-pane="texture">
      <div className="pp-card">
        <PpHead>Textures</PpHead>
        <div className="pp-cats" role="group" aria-label="Texture categories">
          {[{ id: 'featured', label: 'Featured' }, ...TEXTURE_CATEGORIES].map((c) => (
            <button
              key={c.id}
              type="button"
              data-cat={c.id}
              className={`pp-cat${cat === c.id ? ' active' : ''}`}
              onClick={() => setCat(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="pp-grid pp-tex-grid" role="group" aria-label="Textures">
          {shown.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`pp-swatch pp-tex pp-tex-card${activeId === t.id ? ' active' : ''}`}
              title={t.label}
              aria-label={`Texture ${t.label}`}
              data-texture-id={t.id}
              onClick={() => emit({ type: 'texture', id: t.id, scale })}
            >
              <span className="pp-tex-thumb">
                <img src={textureUrl(t.id)} alt="" loading="lazy" draggable={false} />
              </span>
              <span className="pp-tex-name">{t.label}</span>
            </button>
          ))}
        </div>
      </div>
      {allowImport && userTextures.length > 0 && (
        <div className="pp-card">
          <PpHead>Your textures</PpHead>
          <div className="pp-grid pp-tex-grid" role="group" aria-label="Your imported textures">
            {userTextures.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`pp-swatch pp-tex pp-tex-card pp-user-tex${activeSrc === t.src ? ' active' : ''}`}
                title={`${t.label} — right-click or hold to remove`}
                aria-label={`Imported texture ${t.label}`}
                data-texture-id={t.id}
                onClick={() => emit({ type: 'texture', src: t.src, scale })}
                {...removableProps(() => userTextureStore.remove((e) => e.id === t.id))}
              >
                <span className="pp-tex-thumb">
                  <img src={t.src} alt="" draggable={false} />
                </span>
                <span className="pp-tex-name">{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {isTex && (
        <PpSlider
          sliderId="texScale"
          label="Pattern size"
          readout={`${Math.round(scale * 100)}%`}
          value={Math.round(scale * 100)}
          min={25}
          max={400}
          step={5}
          onChange={(v) => emit({ type: 'texture', ...(activeSrc ? { src: activeSrc } : { id: activeId }), scale: v / 100 }, { live: true })}
          onEnd={endStroke}
        />
      )}
      {allowImport && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; importFile(f); }}
          />
          <button type="button" className="pp-tex-import" onClick={() => fileRef.current?.click()}>
            ⬇ Import Texture…
          </button>
        </>
      )}
      {onBrowseAll && (
        <button type="button" className="pp-browse" onClick={onBrowseAll}>
          Browse all textures…
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// eyedropper — window.EyeDropper when the runtime ships it; otherwise a
// canvas-sampling overlay: click samples the fabric LOWER canvas at DPR
// (same-origin by the texture crossOrigin law, so getImageData stays clean);
// Esc cancels.
function EyedropOverlay({ onPick, onCancel }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onCancel(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const sample = (e) => {
    const canvas = document.querySelector('canvas.lower-canvas');
    if (!canvas) { onCancel(); return; }
    const r = canvas.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) {
      onCancel(); // clicked outside the artwork — quiet exit
      return;
    }
    try {
      const x = Math.min(canvas.width - 1, Math.max(0, Math.round((e.clientX - r.left) * (canvas.width / r.width))));
      const y = Math.min(canvas.height - 1, Math.max(0, Math.round((e.clientY - r.top) * (canvas.height / r.height))));
      const d = canvas.getContext('2d').getImageData(x, y, 1, 1).data;
      onPick(formatColor({ r: d[0], g: d[1], b: d[2], a: 1 }));
    } catch { onCancel(); } // tainted canvas etc. — never crash the picker
  };

  return createPortal(
    <div
      className="pp-eyedrop-overlay"
      title="Click the artwork to pick its color — Esc cancels"
      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); sample(e); }}
    />,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// PaintPicker — the panel

const PANES = [
  { id: 'solid', label: 'Color', icon: '◐' },
  { id: 'gradient', label: 'Gradient', icon: '▨' },
  { id: 'texture', label: 'Texture', icon: '✳' },
];

export default function PaintPicker({
  value,
  onChange,
  onCommit,
  allow = ['solid', 'gradient', 'texture'],
  density = 'compact',
  recentKey = 'global',
  palette = DEFAULT_PALETTE,
  linearOnly = false,
  onBrowseAll,
  className = '',
  /** Engines that cannot carry alpha (three.js plate/sides, id-encoded
   * params): hides the Opacity bar and clamps solid emissions to '#rrggbb'. */
  opaque = false,
  /** Texture imports need a src-capable host; id-encoded hosts pass false. */
  allowImport = true,
  /** Popover hosts pass this so Apply can commit-and-close. */
  onRequestClose,
}) {
  const panes = PANES.filter((p) => allow.includes(p.id));
  const kind = paintKind(value);
  const derivedTab = kind === 'gradient' && allow.includes('gradient') ? 'gradient'
    : kind === 'texture' && allow.includes('texture') ? 'texture'
      : 'solid';
  // Browsing override (the FillSection/ColorFlyout pattern): tabs browse,
  // only a pick changes the paint; the override drops when the paint moves.
  const [uiTab, setUiTab] = useState(null);
  const valueKey = typeof value === 'string' ? value : JSON.stringify(value ?? null);
  useEffect(() => { setUiTab(null); }, [valueKey]);
  const tab = uiTab ?? derivedTab;

  // Recents now render via LibraryShelf (colorLibrary store) — the pane-local
  // pseRecentPaints.v1 read is retired here. rememberPaint still writes that v1
  // ring below (kept warm for any external reader); nothing in this panel
  // subscribes to it anymore.
  const lastEmitted = useRef(null);
  // Clear = revert-to-open (the styles-modal Revert law): snapshot the value
  // this panel MOUNTED with; Clear writes it back through the live pipe.
  const openSnapshot = useRef(value);
  const [eyedropOpen, setEyedropOpen] = useState(false);

  // emit: the ONE gate every pane speaks through. Discrete picks are
  // remembered immediately; drag ticks are remembered when the stroke ends.
  const emit = (next, meta = {}, opts = {}) => {
    let out = next;
    if (opaque && typeof out === 'string') out = normalizeOpaqueHex(out, '#000000');
    lastEmitted.current = out;
    onChange?.(out, { live: !!meta?.live });
    if (!meta?.live && !opts.skipRemember) {
      rememberPaint(recentKey, out);
      const k = kindOfFill(out);
      if (k) noteRecent(k, out);
    }
  };
  const endStroke = () => {
    onCommit?.();
    if (lastEmitted.current != null) {
      rememberPaint(recentKey, lastEmitted.current);
      const k = kindOfFill(lastEmitted.current);
      if (k) noteRecent(k, lastEmitted.current);
    }
  };

  const runEyedropper = () => {
    if (typeof window !== 'undefined' && window.EyeDropper) {
      new window.EyeDropper().open()
        .then((res) => { if (res?.sRGBHex) emit(res.sRGBHex.toLowerCase()); })
        .catch(() => {}); // user dismissed — quiet
    } else {
      setEyedropOpen(true);
    }
  };

  return (
    <div
      className={`pp-root${className ? ` ${className}` : ''}`}
      data-density={density}
      data-pane={tab}
      data-panes={panes.length > 1 ? 'multi' : 'single'}
    >
      {panes.length > 1 && (
        <div className="pp-tabs" role="tablist" aria-label="Paint type">
          {panes.map((p) => (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={tab === p.id}
              data-tab={p.id}
              className={`pp-tab${tab === p.id ? ' active' : ''}`}
              onClick={() => setUiTab(p.id)}
            >
              <span className="pp-tab-icon" aria-hidden="true">{p.icon}</span>
              {p.label}
            </button>
          ))}
        </div>
      )}
      {/* Recent stays TOP-LEVEL on every tab (owner 2026-07-23) and the shelf
          caps it to a single row. */}
      <LibraryShelf
        section="recent"
        kind={tab === 'solid' ? 'color' : tab}
        currentValue={value}
        onPick={(v) => emit(v)}
        density={density}
      />
      {tab === 'solid' && (
        <SolidPane
          value={value}
          emit={emit}
          endStroke={endStroke}
          palette={palette}
          opaque={opaque}
          suggestSlot={(
            <LibraryShelf
              section="suggest"
              kind="color"
              currentValue={value}
              onPick={(v) => emit(v)}
              density={density}
              getPaletteCanvas={getRegisteredPaletteCanvas}
            />
          )}
        />
      )}
      {tab === 'gradient' && (
        <GradientPane value={value} emit={emit} endStroke={endStroke} linearOnly={linearOnly} opaque={opaque} />
      )}
      {tab === 'texture' && (
        <TexturePane
          value={value}
          emit={emit}
          endStroke={endStroke}
          onBrowseAll={onBrowseAll}
          allowImport={allowImport && !opaque}
        />
      )}
      {/* My galleries — the ENTIRE galleries block (galleries + New gallery +
          save-star) folds into a collapsed accordion, on every tab. */}
      <Accordion id="galleries" title="My galleries">
        <LibraryShelf
          section="galleries"
          kind={tab === 'solid' ? 'color' : tab}
          currentValue={value}
          onPick={(v) => emit(v)}
          density={density}
        />
      </Accordion>
      <div className="pp-footer">
        <button
          type="button"
          className="pp-footer-btn pp-eyedrop"
          title="Pick a color from the artwork"
          onClick={runEyedropper}
        >
          <span aria-hidden="true">🖊</span> Eyedropper
        </button>
        <span className="pp-footer-spacer" />
        <button
          type="button"
          className="pp-footer-btn pp-clear"
          title="Put back the paint this panel opened with"
          disabled={openSnapshot.current == null}
          onClick={() => {
            if (openSnapshot.current == null) return;
            emit(openSnapshot.current, {}, { skipRemember: true });
            onCommit?.();
          }}
        >
          Clear
        </button>
        <button
          type="button"
          className="pp-footer-btn pp-apply"
          title="Keep this paint"
          onClick={() => { onCommit?.(); onRequestClose?.(); }}
        >
          Apply
        </button>
      </div>
      {eyedropOpen && (
        <EyedropOverlay
          onPick={(c) => { setEyedropOpen(false); emit(c); }}
          onCancel={() => setEyedropOpen(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PaintField — the compact chip. Clicking opens the ONE house paint modal
// (owner 2026-07-21) with this field's exact props + title; the old
// body-portaled popover is GONE (its pane expansion / scrollbars were the
// complaint the modal fixes). The inline chip rendering is unchanged, so every
// host — drawer fills, effect params, plate/side color, the ribbon fallbacks
// via TbColorField — inherits the modal in this one change. dataField becomes
// the modal's stable probe hook (data-paint-modal) and recentKey/palette/allow/
// opaque/linearOnly/onBrowseAll pass straight through the modal to PaintPicker.
export function PaintField({
  label,
  value,
  onChange,
  onCommit,
  allow = ['solid'],
  density = 'compact',
  recentKey = 'global',
  palette,
  linearOnly = false,
  onBrowseAll,
  chipClassName = '',
  dataField,
  opaque = false,
  allowImport = true,
}) {
  return (
    <PaintChip
      value={value}
      title={label}
      className={`pp-field-chip${chipClassName ? ` ${chipClassName}` : ''}`}
      ariaHaspopup="dialog"
      onClick={() => openPaintModal({
        fieldId: dataField || label,
        title: label,
        value,
        onChange,
        onCommit,
        allow,
        density,
        recentKey,
        palette,
        linearOnly,
        opaque,
        allowImport,
        onBrowseAll,
      })}
    />
  );
}
